import type { ReactNode } from "react";
import { useLocation, useParams } from "react-router-dom";
import { ErrorBoundary } from "@a2z/observability";
import { useLocale } from "@/app/locale";
import { RouteErrorFallback } from "./RouteErrorFallback";

/**
 * Route-content error boundary. Sits *inside* a layout (around `<Outlet/>`),
 * so a render error in a page is contained to the content area while the
 * surrounding shell stays mounted and usable. Caught errors are still
 * reported to Sentry (this re-exports `@sentry/react`'s ErrorBoundary).
 *
 * `key={pathname}` remounts the boundary on every navigation, which clears
 * a latched error automatically — clicking a sidebar link recovers without
 * a manual reset. The in-pane fallback also offers an explicit retry.
 */
export function RouteErrorBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const locale = useLocale();
  const { storeSlug = "" } = useParams();
  const homeHref = `/${locale}/${storeSlug}`;

  return (
    <ErrorBoundary
      key={pathname}
      fallback={({ resetError }) => (
        <RouteErrorFallback onRetry={resetError} homeHref={homeHref} />
      )}
    >
      {children}
    </ErrorBoundary>
  );
}
